import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const WEEKDAYS = ["S", "T", "Q", "Q", "S", "S", "D"];

const buildGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  const offset = (first.getDay() + 6) % 7;
  start.setDate(first.getDate() - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

const ClientCalendar = ({ occupied = [] }) => {
  const now = new Date();
  const [cursor, setCursor] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const occupiedSet = useMemo(() => new Set(occupied), [occupied]);

  const grid = useMemo(() => buildGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const monthLabel = `${MONTHS_PT[cursor.getMonth()]} ${cursor.getFullYear()}`;

  const totalDays = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  let monthBooked = 0;
  for (let d = 1; d <= totalDays; d++) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    if (occupiedSet.has(key)) monthBooked++;
  }
  const monthOcc = Math.round((monthBooked / totalDays) * 100);

  return (
    <div data-testid="client-calendar">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <div className="overline text-[#C5A059]">Calendário</div>
          <h3 className="font-serif-display text-2xl md:text-3xl text-[#0A1128] capitalize mt-1">{monthLabel}</h3>
          <p className="text-xs text-[#3A435E] mt-1">
            <span className="text-[#0A1128] font-medium">{monthBooked}</span> noites reservadas · {monthOcc}% de ocupação
          </p>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="w-9 h-9 border border-[#0A1128]/15 grid place-items-center hover:bg-[#0A1128] hover:text-white transition-colors"
            data-testid="client-cal-prev"
          ><ChevronLeft size={14} strokeWidth={1.4} /></button>
          <button
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="w-9 h-9 border border-[#0A1128]/15 grid place-items-center hover:bg-[#0A1128] hover:text-white transition-colors"
            data-testid="client-cal-next"
          ><ChevronRight size={14} strokeWidth={1.4} /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-[10px] tracking-[0.2em] text-[#3A435E] mb-2 px-1">
        {WEEKDAYS.map((w, i) => (<div key={`wd-${i}-${w}`} className="text-center">{w}</div>))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((d) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const isOcc = occupiedSet.has(key);
          const isToday = d.toDateString() === now.toDateString();
          return (
            <div
              key={key}
              className={`aspect-square flex items-center justify-center text-xs relative transition-all ${
                !inMonth ? "text-[#0A1128]/20 bg-transparent" :
                isOcc ? "bg-[#0A1128] text-[#F4F1EB]" :
                "bg-[#F4F1EB]/70 text-[#0A1128]"
              } ${isToday ? "ring-2 ring-[#C5A059]" : ""}`}
              title={isOcc ? "Reservado" : inMonth ? "Disponível" : ""}
            >
              {d.getDate()}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex gap-5 mt-5 text-xs text-[#3A435E]">
        <span className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-[#0A1128]" /> Reservado</span>
        <span className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-[#F4F1EB] border border-[#0A1128]/20" /> Disponível</span>
        <span className="flex items-center gap-2"><span className="inline-block w-3 h-3 border-2 border-[#C5A059]" /> Hoje</span>
      </div>
    </div>
  );
};

export default ClientCalendar;
